import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import { ICashFlowStatement, ICashFlowStatementEntry } from '@modeso/types__aumico-lib-annualreport-be';

export class CashFlowStatementPrintPreviewHelper {
    static shouldDisplayFLI(item: ICashFlowStatementEntry, isHideZeroFLIs: boolean): boolean {
        return !(isHideZeroFLIs && this.isRowZeroOrEmpty(item));
    }

    private static isRowZeroOrEmpty(item: ICashFlowStatementEntry): boolean {
        const hasManuallyEnteredCurrentValue = item.manuallyEnteredCurrentValue != null && NumberUtils.isFinite(item.manuallyEnteredCurrentValue);
        const hasManuallyEnteredPreviousValue = item.manuallyEnteredPreviousValue != null && NumberUtils.isFinite(item.manuallyEnteredPreviousValue);
        let currentValue: number | null = null;
        let previousValue: number | null = null;
        if (hasManuallyEnteredCurrentValue) {
            currentValue = item.manuallyEnteredCurrentValue;
        } else {
            currentValue = item.calculatedCurrentValue;
        }

        if (hasManuallyEnteredPreviousValue) {
            previousValue = item.manuallyEnteredPreviousValue;
        } else {
            previousValue = item.calculatedPreviousValue;
        }

        const isCurrentValueZeroOrEmpty =
            currentValue == null ||
            !NumberUtils.isFinite(currentValue) ||
            NumberUtils.isZero(currentValue);

        const isPreviousValueZeroOrEmpty =
            previousValue == null ||
            !NumberUtils.isFinite(previousValue) ||
            NumberUtils.isZero(previousValue);

        return isCurrentValueZeroOrEmpty && isPreviousValueZeroOrEmpty;
    }

    public static calculateActivityCurrentYearTotal(
        entries: ICashFlowStatementEntry[],
        numberOfDecimalPlacesValues: number,
    ): number {
        const entriesValues = entries.map<number | null>((entry) => entry.manuallyEnteredCurrentValue != null ? entry.manuallyEnteredCurrentValue : entry.calculatedCurrentValue);
        return NumberUtils.sumIgnoringInfiniteNumbers(entriesValues, 0, false, numberOfDecimalPlacesValues);
    }

    public static calculateActivityPreviousYearTotal(
        entries: ICashFlowStatementEntry[],
        numberOfDecimalPlacesValues: number,
    ): number {
        const entriesValues = entries.map<number | null>((entry) => entry.manuallyEnteredPreviousValue != null ? entry.manuallyEnteredPreviousValue : entry.calculatedPreviousValue);
        return NumberUtils.sumIgnoringInfiniteNumbers(entriesValues, 0, false, numberOfDecimalPlacesValues);
    }

    public static calculateAllActivitiesPreviousYearTotals(
        cashFlowStatement: ICashFlowStatement,
        numberOfDecimalPlacesValues: number,
    ): number {
        // operating Activities
        const operatingActivitiesPreviousTotal = CashFlowStatementPrintPreviewHelper.calculateActivityPreviousYearTotal(
            cashFlowStatement.operatingActivities.entries,
            numberOfDecimalPlacesValues,
        );

        // investing activities
        const investingActivitiesPreviousTotal = CashFlowStatementPrintPreviewHelper.calculateActivityPreviousYearTotal(
            cashFlowStatement.investingActivities.entries,
            numberOfDecimalPlacesValues,
        );

        // financing activities
        const financingActivitiesPreviousTotal = CashFlowStatementPrintPreviewHelper.calculateActivityPreviousYearTotal(
            cashFlowStatement.financingActivities.entries,
            numberOfDecimalPlacesValues,
        );

        return NumberUtils.sumNumbers([
            operatingActivitiesPreviousTotal,
            investingActivitiesPreviousTotal,
            financingActivitiesPreviousTotal,
        ]);
    }

    public static calculateAllActivitiesCurrentYearTotals(
        cashFlowStatement: ICashFlowStatement,
        numberOfDecimalPlacesValues: number,
    ): number {
        // operating Activities
        const operatingActivitiesCurrentTotal = CashFlowStatementPrintPreviewHelper.calculateActivityCurrentYearTotal(
            cashFlowStatement.operatingActivities.entries,
            numberOfDecimalPlacesValues,
        );

        // investing activities
        const investingActivitiesCurrentTotal = CashFlowStatementPrintPreviewHelper.calculateActivityCurrentYearTotal(
            cashFlowStatement.investingActivities.entries,
            numberOfDecimalPlacesValues,
        );

        // financing activities
        const financingActivitiesCurrentTotal = CashFlowStatementPrintPreviewHelper.calculateActivityCurrentYearTotal(
            cashFlowStatement.financingActivities.entries,
            numberOfDecimalPlacesValues,
        );

        return NumberUtils.sumNumbers([
            operatingActivitiesCurrentTotal,
            investingActivitiesCurrentTotal,
            financingActivitiesCurrentTotal,
        ]);
    }

    public static getAttachmentReferencesOfFli(fliId: string, attachmentReferences: Map<string, string>): string {
        if (attachmentReferences == null) {
            return '';
        }
        const fliAttachmentReferences: string = attachmentReferences.get(fliId);

        return fliAttachmentReferences != null ? fliAttachmentReferences : '';
    }
}
